import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthDepartments } from '@/api/auth';
import { getCategoryList, type CategoryListItem } from '@/api/category';
import {
  deletePurchaseMonthDetail,
  exportPurchaseMonthSummary,
  getPurchaseMonthDetails,
  type PurchaseMonthDetailRow,
} from '@/api/purchase';
import MonthPicker, { currentMonthValue } from '@/components/base/MonthPicker';
import SearchableSelect from '@/components/base/SearchableSelect';
import ConfirmModal from '@/pages/purchase-plan/components/ConfirmModal';
import { isPurchasePlanManager } from '@/utils/auth';
import { showToast } from '@/utils/message';

const EXPORT_PLAN_TYPES = [
  { value: 1 as const, label: '全院计划' },
  { value: 4 as const, label: '全院科室汇总' },
  { value: 2 as const, label: '科室计划' },
  { value: 3 as const, label: '个人计划' },
];

function parseMonthParam(value: string | null): string {
  const raw = String(value || '').trim();
  if (/^\d{4}-\d{2}$/.test(raw)) return raw;
  if (/^\d{6}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`;
  return currentMonthValue();
}

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function qtyClass(v: number) {
  if (v < 0) return 'text-orange-600 font-medium';
  if (v > 0) return 'text-foreground-700';
  return 'text-foreground-400';
}

function diffClass(v: number) {
  if (v > 0) return 'text-primary-600 font-medium';
  if (v < 0) return 'text-orange-600 font-medium';
  return 'text-foreground-400';
}

function pickTopCategories(list: CategoryListItem[]) {
  const top = list.filter((item) => !Number(item.pid));
  return top.length > 0 ? top : list;
}

export default function PurchasePlanMonthDetails() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isManager = isPurchasePlanManager();

  const [month, setMonth] = useState(() => parseMonthParam(searchParams.get('month')));
  const [keywordInput, setKeywordInput] = useState(() => searchParams.get('keyword') || '');
  const [keyword, setKeyword] = useState(() => searchParams.get('keyword') || '');
  const [deptTitle, setDeptTitle] = useState('');
  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categories, setCategories] = useState<CategoryListItem[]>([]);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<PurchaseMonthDetailRow[]>([]);
  const [monthLabel, setMonthLabel] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [pendingDelete, setPendingDelete] = useState<PurchaseMonthDetailRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const departmentId = deptTitle ? deptIdMap[deptTitle] : undefined;
  const categoryId = useMemo(() => {
    if (!categoryTitle) return undefined;
    const hit = categories.find((item) => String(item.title || '').trim() === categoryTitle);
    const id = Number(hit?.id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }, [categories, categoryTitle]);

  const categoryOptions = useMemo(
    () =>
      categories
        .map((item) => String(item.title || '').trim())
        .filter(Boolean),
    [categories],
  );

  const syncMonthToUrl = useCallback(
    (nextMonth: string) => {
      const current = parseMonthParam(searchParams.get('month'));
      if (current === nextMonth) return;
      const next = new URLSearchParams(searchParams);
      next.set('month', nextMonth);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleMonthChange = (value: string) => {
    const next = parseMonthParam(value);
    setMonth(next);
    syncMonthToUrl(next);
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        if (!ok || !Array.isArray(res.data)) return;
        const nextMap: Record<string, string> = {};
        const nextOptions: string[] = [];
        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (!nextMap[title]) {
            nextMap[title] = String(id);
            nextOptions.push(title);
          }
        });
        setDeptIdMap(nextMap);
        setDeptOptions(nextOptions);
      } catch {
        if (!cancelled) {
          setDeptIdMap({});
          setDeptOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getCategoryList();
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        const list = pickTopCategories(Array.isArray(res.data) ? res.data : []);
        setCategories(ok ? list : []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getPurchaseMonthDetails({
        month,
        department_id: departmentId,
        category_id: categoryId,
        keyword: keyword.trim() || undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setRows([]);
        setTotal(0);
        setLoadError(res.message || res.msg || '月汇总明细加载失败');
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotal(Number(res.total ?? 0) || 0);
      setMonthLabel(String(res.apply_month_text || '').trim());
      setDepartmentName(String(res.department_name || '').trim());
    } catch {
      setRows([]);
      setTotal(0);
      setLoadError('月汇总明细加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [categoryId, departmentId, keyword, month]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setKeyword(keywordInput.trim());
  };

  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [exportMenuOpen]);

  const handleExport = async (controlType: 1 | 2 | 3 | 4) => {
    if (exporting) return;
    const typeLabel = EXPORT_PLAN_TYPES.find((item) => item.value === controlType)?.label || '计划';
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const res = await exportPurchaseMonthSummary({ month, control_type: controlType });
      const ok = res.error === false || Number(res.error) === 0;
      const fileUrl = String(res.url || '').trim();
      if (!ok || !fileUrl) {
        showToast(res.message || res.msg || '导出失败', { type: 'error' });
        return;
      }
      const fromUrl = decodeURIComponent(fileUrl.split('/').pop()?.split('?')[0] || '');
      const filename =
        String(res.filename || '').trim() ||
        fromUrl ||
        `${typeLabel}月汇总表.xlsx`;
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast(res.message || `${typeLabel}月汇总表已开始下载`, { type: 'success' });
    } catch {
      showToast('导出失败，请稍后重试', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const requestDelete = (row: PurchaseMonthDetailRow) => {
    if (deleting || exporting) return;
    if (!(Number(row.goods_id) > 0)) return;
    setPendingDelete(row);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete || deleting) return;
    const goodsId = Number(pendingDelete.goods_id);
    if (!Number.isFinite(goodsId) || goodsId < 1) {
      showToast('物资无效，无法删除', { type: 'error' });
      return;
    }
    setDeleting(true);
    try {
      const res = await deletePurchaseMonthDetail({
        month,
        goods_id: goodsId,
        spec_id: Number(pendingDelete.spec_id) > 0 ? Number(pendingDelete.spec_id) : 0,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除明细失败', { type: 'error' });
        return;
      }
      showToast(res.message || '计划明细已删除', { type: 'success' });
      setPendingDelete(null);
      void fetchList();
    } catch {
      showToast('网络异常，删除失败', { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const title = useMemo(() => {
    const label = monthLabel || `${month.slice(0, 4)}年${month.slice(5, 7)}月`;
    return `${label}全院计划月汇总明细`;
  }, [month, monthLabel]);

  if (!isManager) {
    return <Navigate to="/403" replace />;
  }

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6 pb-4 md:pb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-44 shrink-0">
              <MonthPicker
                value={month}
                onChange={handleMonthChange}
                placeholder="选择计划月份"
              />
            </div>
            {categoryOptions.length > 0 && (
              <div className="min-w-[12rem] max-w-xs flex-1 basis-[12rem]">
                <SearchableSelect
                  options={categoryOptions}
                  value={categoryTitle}
                  onChange={(v) => {
                    setCategoryTitle(typeof v === 'string' ? v : v[0] || '');
                  }}
                  placeholder="全部分类"
                  allLabel="全部分类"
                  icon="ri-price-tag-3-line"
                  searchPlaceholder="搜索分类..."
                />
              </div>
            )}
            {deptOptions.length > 0 && (
              <div className="min-w-[14rem] max-w-xs flex-1 basis-[14rem]">
                <SearchableSelect
                  options={deptOptions}
                  value={deptTitle}
                  onChange={(v) => {
                    setDeptTitle(typeof v === 'string' ? v : v[0] || '');
                  }}
                  placeholder="搜索 / 选择科室"
                  allLabel="不选则默认本科室"
                  icon="ri-hospital-line"
                  searchPlaceholder="继续搜索科室..."
                />
              </div>
            )}
            <div className="relative min-w-[14rem] max-w-sm flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder="搜索物资名称 / 规格 / 编码"
                className="w-full h-[38px] pl-9 pr-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors shrink-0"
            >
              <i className="ri-search-line" />
              查询
            </button>
            <button
              type="button"
              onClick={() => navigate('/purchase-plan')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-600 hover:bg-background-50 shrink-0 ml-auto"
            >
              <i className="ri-arrow-left-line" />
              返回列表
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-foreground-900 truncate">{title}</h1>
              {departmentName ? (
                <p className="text-xs text-foreground-500 mt-0.5">对照科室：{departmentName}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-foreground-500 whitespace-nowrap">共 {total} 项</span>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/purchase-plan/department-submit-status?month=${encodeURIComponent(month)}`,
                  )
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary-300 text-primary-600 hover:bg-primary-50 text-xs font-medium transition-colors"
              >
                <i className="ri-building-line" />
                全院提交情况
              </button>
              <div className="relative" ref={exportMenuRef}>
                <button
                  type="button"
                  onClick={() => setExportMenuOpen((open) => !open)}
                  disabled={exporting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-xs font-medium transition-colors"
                >
                  <i className={exporting ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} />
                  {exporting ? '导出中...' : '导出汇总表'}
                  {!exporting && <i className="ri-arrow-down-s-line text-sm" />}
                </button>
                {exportMenuOpen && !exporting && (
                  <div className="absolute right-0 top-full mt-1 z-20 min-w-[9.5rem] rounded-md border border-background-200 bg-white shadow-lg py-1">
                    {EXPORT_PLAN_TYPES.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => void handleExport(item.value)}
                        className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
              <thead>
                <tr className="bg-[#f8f8f9]">
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-14">
                    序号
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    物资类别
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    物资名称
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    规格名称
                  </th>
                  <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    规格编码
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    单位
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    全院计划数
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    科室计划数
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    个人计划数
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    科室-全院
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                    个人-科室
                  </th>
                  <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-16">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={12} className="py-16 text-center text-foreground-400 text-sm">
                      <i className="ri-loader-4-line animate-spin mr-1.5" />
                      正在加载月汇总明细...
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={12} className="py-16 text-center text-sm">
                      <p className="text-foreground-500 mb-2">{loadError}</p>
                      <button
                        type="button"
                        onClick={() => void fetchList()}
                        className="text-primary-600 hover:text-primary-700 text-xs"
                      >
                        重新加载
                      </button>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="py-16 text-center text-foreground-400 text-sm">
                      该月暂无计划汇总明细
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const hospitalQty = n(row.hospital_plan_quantity);
                    const deptQty = n(row.department_plan_quantity);
                    const accountQty = n(row.account_plan_quantity ?? row.plan_quantity);
                    const diffDept = n(row.diff_department_hospital);
                    const diffAccount = n(row.diff_account_department);
                    return (
                      <tr
                        key={`${row.goods_id}-${row.spec_id}-${idx}`}
                        className="hover:bg-background-50 transition-colors"
                      >
                        <td className="py-2.5 px-3 text-center text-foreground-500 text-xs">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">
                          {row.category_name || '未分类'}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[180px] truncate">
                          {row.goods_name || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">
                          {row.spec_name || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">
                          {row.code || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 whitespace-nowrap text-xs">
                          {row.unit || '-'}
                        </td>
                        <td className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${qtyClass(hospitalQty)}`}>
                          {hospitalQty}
                        </td>
                        <td className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${qtyClass(deptQty)}`}>
                          {deptQty}
                        </td>
                        <td className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${qtyClass(accountQty)}`}>
                          {accountQty}
                        </td>
                        <td className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${diffClass(diffDept)}`}>
                          {diffDept}
                        </td>
                        <td className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${diffClass(diffAccount)}`}>
                          {diffAccount}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <button
                            type="button"
                            disabled={deleting}
                            onClick={() => requestDelete(row)}
                            title="删除计划明细"
                            className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!pendingDelete}
        title="删除计划明细"
        message={`确认删除「${pendingDelete?.goods_name || '该物资'}」${
          pendingDelete?.spec_name ? `（${pendingDelete.spec_name}）` : ''
        }的计划明细？删除后不可恢复。`}
        confirmLabel="确认删除"
        danger
        loading={deleting}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={() => void handleDeleteConfirm()}
      />
    </div>
  );
}
